import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  DataAccessEventsService,
  DataAccessExhibitorService,
  DataAccessFairsFacade,
  DataAccessPostsService,
  DataAccessProductsService,
} from '@planone/bau365/data-access-fair-data';
import { EntityType, Event, Exhibitor, Post, Product } from '@planone/bau365/util-fair-data';
import { catchError, map, mergeMap, Observable, of } from 'rxjs';

import { USER_SERVICE_CONFIG, UserServiceConfigInterface } from '../../config/user-service.config.interface';
import { EnrichmentData } from '../../state/enriched-watchlist/enriched-watchlist.models';
import { WatchlistDocument, WatchlistItem, WatchlistItemIdentity } from '../../state/watchlist/watchlist.models';

@Injectable()
export class WatchlistService {
  userApiURL: string | null = null;
  onboardingApiURL: string | null = null;
  keycloakApiURL: string | null = null;

  fairId$: Observable<string>;

  constructor(
    private httpClient: HttpClient,
    private postsService: DataAccessPostsService,
    private productsService: DataAccessProductsService,
    private eventService: DataAccessEventsService,
    private exhibitorsService: DataAccessExhibitorService,
    private fairsFacade: DataAccessFairsFacade,
    @Inject(USER_SERVICE_CONFIG) userServiceConfig: UserServiceConfigInterface
  ) {
    this.keycloakApiURL = userServiceConfig.keycloakApiURL;
    this.userApiURL = userServiceConfig.userServiceApiURL;
    this.onboardingApiURL = userServiceConfig.fairDataGatewayURL;
    this.fairId$ = this.fairsFacade.fairId$;
  }

  getWatchlistItems() {
    return this.httpClient.get<{
      items: WatchlistItem[];
      modified_at: string;
    }>(`${this.userApiURL}/full/watchlist`);
  }

  getEnrichedWatchlistItem(item: WatchlistItem): Observable<EnrichmentData> {
    const { entity_id, fair_id } = item;
    switch (item.entity_type) {
      case EntityType.exhibitor:
        return this.exhibitorsService.getExhibitor(fair_id, entity_id).pipe(
          map((exhibitor) => this.mapWatchlistExhibitor(item, exhibitor)),
          catchError((error) => this.combineWatchlistError(item, error))
        );
      case EntityType.post:
        return this.postsService.getPost(fair_id, entity_id).pipe(
          map((post) => this.mapWatchlistPost(item, post)),
          catchError((error) => this.combineWatchlistError(item, error))
        );
      case EntityType.product:
        return this.productsService.getProduct(fair_id, entity_id).pipe(
          map((product) => this.mapWatchlistProduct(item, product)),
          mergeMap((enrichedItem) => {
            if (!enrichedItem.data || !enrichedItem.data.exhibitorId) return of(enrichedItem);
            const enrichedData = enrichedItem.data;
            return this.exhibitorsService.getExhibitor(fair_id, enrichedItem.data.exhibitorId).pipe(
              map((exhibitor) => {
                return {
                  ...enrichedItem,
                  data: {
                    ...enrichedData,
                    exhibitorName: exhibitor.name,
                  },
                };
              })
            );
          }),
          catchError((error) => this.combineWatchlistError(item, error))
        );
      case EntityType.event:
        return this.eventService.getEvent(fair_id, entity_id).pipe(
          map((event) => this.mapWatchlistEvent(item, event)),
          mergeMap((enrichedItem) => {
            if (!enrichedItem.data?.exhibitorId) return of(enrichedItem);
            return this.exhibitorsService.getExhibitor(fair_id, enrichedItem.data.exhibitorId).pipe(
              map((exhibtor) => {
                return {
                  ...enrichedItem,
                  data: {
                    ...enrichedItem.data,
                    image: exhibtor.mood_image,
                  },
                };
              })
            );
          }),
          catchError((error) => this.combineWatchlistError(item, error))
        );
      case EntityType.brand: {
        const exhibitorId = entity_id.slice(0, 36);
        return this.exhibitorsService.getExhibitor(fair_id, exhibitorId).pipe(
          map((exhibitor) => this.mapWatchlistBrand(item, exhibitor)),
          catchError((error) => this.combineWatchlistError(item, error))
        );
      }
      default:
        return of(item);
    }
  }

  addWatchlistItem({ fair_id, entity_id, entity_type }: WatchlistItemIdentity): Observable<WatchlistItem> {
    entity_type === 'BRAND' ? (entity_id = entity_id.replace(/\//g, '-slash-')) : '';

    return this.httpClient.put<WatchlistItem>(
      `${this.userApiURL}/fair/${fair_id}/watchlist/${entity_type}/${entity_id}`,
      null
    );
  }

  removeWatchlistItem({ entity_id, entity_type, fair_id }: WatchlistItemIdentity) {
    entity_type === 'BRAND' ? (entity_id = entity_id.replace(/\//g, '-slash-')) : '';

    return this.httpClient.delete(`${this.userApiURL}/fair/${fair_id}/watchlist/${entity_type}/${entity_id}`);
  }

  generateWatchlistDocument(): Observable<WatchlistDocument> {
    return this.httpClient.post<WatchlistDocument>(`${this.userApiURL}/full/watchlist/document`, null);
  }

  private mapWatchlistPost(item: WatchlistItem, post: Post): EnrichmentData {
    return {
      ...item,
      data: {
        name: post.headline,
        image: post.hero_image,
      },
    };
  }

  private mapWatchlistProduct(item: WatchlistItem, product: Product): EnrichmentData {
    return {
      ...item,
      data: {
        name: product.name,
        image: product.images ? product.images[0] : undefined,
        exhibitorId: product.exhibitor_id,
        exhibitionHalls: product.exhibition_halls,
      },
    };
  }

  private mapWatchlistExhibitor(item: WatchlistItem, exhibitor: Exhibitor): EnrichmentData {
    return {
      ...item,
      data: {
        name: exhibitor.name,
        image: exhibitor.logo,
        exhibitionHalls: exhibitor.exhibition_halls,
      },
    };
  }

  private mapWatchlistEvent(item: WatchlistItem, event: Event) {
    return {
      ...item,
      data: {
        name: event.headline,
        exhibitorId: event.exhibitor_id,
        starts_at: event.starts_at,
        ends_at: event.ends_at,
      },
    };
  }

  private mapWatchlistBrand(item: WatchlistItem, exhibitor: Exhibitor): EnrichmentData {
    const item_entity_id_replaced = item.entity_id.includes('-slash-')
      ? item.entity_id.replace(/-slash-/g, '/')
      : item.entity_id;
    const bookmarkedBrand = exhibitor.brands?.find((brand) => item_entity_id_replaced.includes(brand.name));
    return {
      ...item,
      data: {
        name: exhibitor.name,
        exhibitorName: exhibitor.name,
        exhibitorId: exhibitor.id,
        brand: bookmarkedBrand,
      },
    };
  }

  private combineWatchlistError(item: WatchlistItem, error: HttpErrorResponse): Observable<EnrichmentData> {
    const ignoreStatusCodes = [404];
    if (ignoreStatusCodes.includes(error.status)) return of({ ...item, deleted: true });
    return of({ ...item, hasError: true });
  }
}
